import { useEffect, useState } from "react";
import { ShieldCheck, ShieldOff, Lock, Unlock, KeyRound, Loader2, Copy, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const err = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;

const secretFromUri = (uri) => {
  try {
    return new URL(uri).searchParams.get("secret") || "";
  } catch {
    return "";
  }
};

export default function AdminSecurity() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setup, setSetup] = useState(null); // {qr_data_url, provisioning_uri, recovery_codes}
  const [confirmCode, setConfirmCode] = useState("");
  const [unlockCode, setUnlockCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);
  const [staff, setStaff] = useState([]);
  const [resetTarget, setResetTarget] = useState("");

  const load = () => {
    api.get("/totp/status")
      .then(({ data }) => {
        setStatus(data);
        if (data.is_primary) {
          api.get("/admin/staff").then(({ data: s }) => setStaff(s.filter((x) => !x.is_self))).catch(() => {});
        }
      })
      .catch(() => toast.error("Failed to load security status"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const beginEnroll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/totp/enroll");
      setSetup(data);
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };

  const confirmEnroll = async () => {
    setBusy(true);
    try {
      await api.post("/totp/confirm", { code: confirmCode });
      toast.success("Authenticator enabled");
      setSetup(null); setConfirmCode(""); load();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };

  const unlock = async () => {
    setBusy(true);
    try {
      await api.post(useRecovery ? "/totp/recover" : "/totp/verify", { code: unlockCode });
      toast.success("Sensitive data unlocked for 15 minutes");
      setUnlockCode(""); setUseRecovery(false); load();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };

  const relock = async () => {
    await api.post("/totp/relock").catch(() => {});
    toast.success("Re-locked");
    load();
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/totp/disable", { code: disableCode });
      toast.success("Authenticator disabled");
      setDisableCode(""); load();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };

  const resetUser = async () => {
    if (!resetTarget) return;
    try {
      await api.post(`/admin/totp/reset/${resetTarget}`);
      toast.success("That user's authenticator was reset");
      setResetTarget("");
    } catch (e) { toast.error(err(e)); }
  };

  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Copied"); };

  if (loading)
    return (
      <div className="flex items-center gap-2 text-slate-500 py-16 justify-center">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading…
      </div>
    );

  return (
    <div className="max-w-2xl" data-testid="admin-security">
      <div className="mb-6">
        <h2 className="font-heading text-2xl tracking-tight font-medium text-slate-900 flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-[#1E3A8A]" /> Security &amp; Data Access
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Taxpayer emails are stored encrypted and shown masked. Set up an authenticator app to unlock
          full email reveal and un-masked CSV exports for 15 minutes at a time.
        </p>
      </div>

      {status.enabled && (
        <div
          className={`rounded-md border p-4 mb-6 flex items-center justify-between gap-4 ${
            status.unlocked ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"
          }`}
          data-testid="unlock-status"
        >
          <div className="flex items-center gap-3">
            {status.unlocked ? <Unlock className="w-5 h-5 text-emerald-700" /> : <Lock className="w-5 h-5 text-slate-500" />}
            <div>
              <p className="font-medium text-slate-900">
                {status.unlocked ? "Sensitive data is unlocked" : "Sensitive data is locked"}
              </p>
              <p className="text-sm text-slate-500">
                {status.unlocked
                  ? `Reveals & full exports work until ${new Date(status.unlocked_until).toLocaleTimeString()}.`
                  : "Reveals show masked emails; exports are masked."}
              </p>
            </div>
          </div>
          {status.unlocked && (
            <Button variant="outline" className="border-slate-300" onClick={relock} data-testid="relock-btn">
              Re-lock now
            </Button>
          )}
        </div>
      )}

      {!status.enabled && !setup && (
        <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="enroll-card">
          <p className="text-slate-700 mb-4">
            You don't have an authenticator set up yet. Setting one up is optional, but it's required to
            reveal or export full taxpayer emails.
          </p>
          <Button onClick={beginEnroll} disabled={busy} className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="enroll-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
            Set up authenticator
          </Button>
        </div>
      )}

      {setup && (
        <div className="bg-white border border-slate-200 rounded-md p-6 space-y-5" data-testid="enroll-flow">
          <div>
            <p className="font-medium text-slate-900 mb-2">1. Scan this QR code</p>
            <p className="text-sm text-slate-500 mb-3">Use Google Authenticator, Authy, or any TOTP app.</p>
            <img src={setup.qr_data_url} alt="Authenticator QR code" className="w-44 h-44 border border-slate-200 rounded-md" data-testid="totp-qr" />
            <details className="mt-3 text-sm">
              <summary className="cursor-pointer text-slate-500">Can't scan? Enter this key manually</summary>
              <code className="inline-block mt-2 break-all text-sm bg-slate-50 px-2 py-1 rounded border border-slate-200 font-mono-data" data-testid="totp-secret">
                {secretFromUri(setup.provisioning_uri)}
              </code>
            </details>
          </div>

          <div className="rounded-md border border-amber-300 bg-amber-50 p-4">
            <p className="font-medium text-amber-900 mb-2 flex items-center justify-between">
              2. Save these recovery codes
              <Button size="sm" variant="outline" className="border-amber-300 h-7" onClick={() => copy(setup.recovery_codes.join("\n"))}>
                <Copy className="w-3.5 h-3.5 mr-1" /> Copy
              </Button>
            </p>
            <p className="text-xs text-amber-800 mb-2">Each can be used once if you lose your phone. They won't be shown again.</p>
            <pre className="grid grid-cols-2 gap-x-6 gap-y-1 font-mono-data text-sm text-amber-900" data-testid="recovery-codes">
              {setup.recovery_codes.map((c) => (<span key={c}>{c}</span>))}
            </pre>
          </div>

          <div>
            <p className="font-medium text-slate-900 mb-2">3. Enter the 6-digit code to confirm</p>
            <div className="flex items-center gap-2">
              <Input inputMode="numeric" maxLength={6} value={confirmCode} onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" className="w-40 font-mono-data" data-testid="confirm-code-input" />
              <Button onClick={confirmEnroll} disabled={busy || confirmCode.length !== 6} className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="confirm-enroll-btn">
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Confirm &amp; enable
              </Button>
            </div>
          </div>
        </div>
      )}

      {status.enabled && !status.unlocked && (
        <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="unlock-card">
          <p className="font-medium text-slate-900 mb-1">Unlock sensitive data</p>
          <p className="text-sm text-slate-500 mb-4">
            Enter the {useRecovery ? "recovery" : "6-digit"} code from your authenticator.
          </p>
          <div className="flex items-center gap-2">
            <Input
              inputMode={useRecovery ? "text" : "numeric"}
              maxLength={useRecovery ? 20 : 6}
              value={unlockCode}
              onChange={(e) => setUnlockCode(useRecovery ? e.target.value : e.target.value.replace(/\D/g, ""))}
              placeholder={useRecovery ? "recovery code" : "123456"}
              className="w-52 font-mono-data"
              data-testid="unlock-code-input"
            />
            <Button onClick={unlock} disabled={busy || !unlockCode} className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="unlock-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Unlock className="w-4 h-4 mr-2" />} Unlock 15 min
            </Button>
          </div>
          <button
            className="text-sm text-slate-500 hover:text-[#1E3A8A] mt-3"
            onClick={() => { setUseRecovery((v) => !v); setUnlockCode(""); }}
            data-testid="toggle-recovery"
          >
            {useRecovery ? "Use authenticator code instead" : "Lost your phone? Use a recovery code"}
          </button>
        </div>
      )}

      {status.enabled && (
        <div className="mt-6 bg-white border border-slate-200 rounded-md p-6" data-testid="manage-card">
          <p className="font-medium text-slate-900 mb-1">Manage authenticator</p>
          <p className="text-sm text-slate-500 mb-3">
            {status.recovery_remaining} recovery code(s) remaining. To turn off 2FA, confirm with a current code.
          </p>
          <div className="flex items-center gap-2">
            <Input inputMode="numeric" maxLength={6} value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" className="w-40 font-mono-data" data-testid="disable-code-input" />
            <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={disable} disabled={busy || disableCode.length !== 6} data-testid="disable-btn">
              <ShieldOff className="w-4 h-4 mr-2" /> Disable
            </Button>
          </div>
        </div>
      )}

      {status.is_primary && staff.length > 0 && (
        <div className="mt-6 bg-white border border-slate-200 rounded-md p-6" data-testid="reset-card">
          <p className="font-medium text-slate-900 mb-1">Reset a team member's authenticator</p>
          <p className="text-sm text-slate-500 mb-3">
            If a staff member loses access, reset their authenticator so they can set up a new one.
          </p>
          <div className="flex items-center gap-2">
            <Select value={resetTarget} onValueChange={setResetTarget}>
              <SelectTrigger className="w-64" data-testid="reset-target-select"><SelectValue placeholder="Choose a team member" /></SelectTrigger>
              <SelectContent>
                {staff.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
              </SelectContent>
            </Select>
            <Button variant="outline" className="border-slate-300" onClick={resetUser} disabled={!resetTarget} data-testid="reset-user-btn">
              <RotateCcw className="w-4 h-4 mr-2" /> Reset
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
